/** Standard identifier systems */
export const NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';
export const EIN_SYSTEM = 'http://hl7.org/fhir/sid/us-ein';
export const NUCC_TAXONOMY_SYSTEM = 'http://nucc.org/provider-taxonomy';
export const STEDI_PAYER_NETWORK_SYSTEM = 'https://www.stedi.com/healthcare/network';
export const CPT_SYSTEM = 'http://www.ama-assn.org/go/cpt';
export const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm';
export const SELFPAY_CODE = 'SELFPAY';

/** Stedi claim extension URLs */
export const STEDI_EXTENSION_BASE = 'http://polarissleep.com/fhir/extensions/stedi';
export const STEDI_CORRELATION_ID_URL = `${STEDI_EXTENSION_BASE}/correlation-id`;
export const STEDI_TRACE_ID_URL = `${STEDI_EXTENSION_BASE}/trace-id`;
export const STEDI_SUBMITTED_AT_URL = `${STEDI_EXTENSION_BASE}/submitted-at`;
export const CLAIM_PLACE_OF_SERVICE_URL = 'http://polarissleep.com/fhir/extensions/place-of-service';
export const CLAIM_IDEMPOTENCY_SYSTEM = 'http://polarissleep.com/fhir/claim-idempotency';
/** Amount already collected from the patient before an insurance claim was created. */
export const CLAIM_PATIENT_PAID_URL = 'http://polarissleep.com/fhir/extensions/patient-paid';
/** ChargeItem extension carrying an extra CPT/HCPCS modifier for the service line. */
export const BILLING_MODIFIER_URL = 'http://polarissleep.com/fhir/extensions/billing-modifier';

/** Claim submission configuration */
export const MAX_PATIENT_CONTROL_NUMBER_LENGTH = 17;
export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_BASE_DELAY_MS = 1000;

/** FHIR Claim.type */
export const CLAIM_TYPE_PROFESSIONAL = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/claim-type', code: 'professional' }],
};

export const DEFAULT_CLAIM_CURRENCY = 'USD' as const;
export const ADJUDICATION_CATEGORY_SUBMITTED = 'submitted';
export const ADJUDICATION_CATEGORY_BENEFIT = 'benefit';
/** Adjudication category code for patient responsibility in ERA (835) */
export const ADJUDICATION_CATEGORY_PR = 'PR';

/** Submitter identity */
export const SUBMITTER_ORG_NAME = 'Polaris Sleep';

/** us-core-birthsex extension URL */
export const BIRTHSEX_EXTENSION_URL = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex';

/** Task system for claim-related tasks */
export const CLAIM_TASK_SYSTEM = 'http://polarissleep.com/task-types';
export const CLAIM_TASK_CODE = 'claim-submission-failure';
/** Task code for claim validation issues surfaced on the review page (distinct from
 * claim-submission-failure so it does NOT trigger retry-claim-on-task-completion). */
export const CLAIM_VALIDATION_TASK_CODE = 'claim-validation-issues';
export const CLAIM_REJECTION_TASK_CODE = 'claim-response-rejection';
export const CLAIM_DENIAL_TASK_CODE = 'claim-response-denial';
/** Task code for claims that need payer-side void/reversal review after cancellation. */
export const CLAIM_CANCELLATION_REVIEW_TASK_CODE = 'claim-cancellation-review';

/** Task codes for the Manual Billing Review queue (same system as CLAIM_TASK_SYSTEM) */
export const TASK_CODE_REVIEW_REJECTED_CLAIM = 'review-rejected-claim';
export const TASK_CODE_REVIEW_ERRANT_CLAIM = 'review-errant-claim';
export const TASK_CODE_RECONCILE_PATIENT_COPAY = 'reconcile-patient-copay';
export const TASK_CODE_COLLECT_PAYMENT_METHOD = 'collect-payment-method';
export const TASK_CODE_PAYMENT_FAILED = 'payment-failed';
export const TASK_CODE_PAYMENT_DISPUTED = 'payment-disputed';
export const TASK_CODE_REFRESH_ELIGIBILITY = 'refresh-eligibility';

/** Stripe identifier systems (used on Patient.identifier, ChargeItem.identifier, etc.) */
export const STRIPE_CUSTOMER_ID_SYSTEM = 'https://stripe.com/customer-id';
export const STRIPE_PAYMENT_INTENT_ID_SYSTEM = 'https://stripe.com/payment-intent-id';

/** Marks a billed ChargeItem as the HSAT order tied to this ServiceRequest id (idempotency + audit). */
export const HSAT_CHARGE_SERVICE_REQUEST_SYSTEM = 'http://polarissleep.com/hsat-charge/service-request';
export const STRIPE_REFUND_ID_SYSTEM = 'https://stripe.com/refund-id';

/** Stripe extension URLs (on Claim / ClaimResponse) */
export const STRIPE_PAYMENT_INTENT_EXTENSION_URL = 'https://polarissleep.com/extensions/stripe-payment-intent-id';
export const STRIPE_REFUND_EXTENSION_URL = 'https://polarissleep.com/extensions/stripe-refund-id';

/** Extension referencing an Encounter (used on PaymentNotice to link upfront charges) */
export const ENCOUNTER_EXTENSION_URL = 'https://polarissleep.com/extensions/encounter-ref';

/** ClaimResponse extension URLs */
export const STEDI_TRANSACTION_ID_URL = `${STEDI_EXTENSION_BASE}/transaction-id`;
export const STEDI_PROCESSED_AT_URL = `${STEDI_EXTENSION_BASE}/processed-at`;
export const STEDI_CHECK_OR_EFT_TRACE_NUMBER_URL = `${STEDI_EXTENSION_BASE}/check-or-eft-trace-number`;
export const STEDI_WEBHOOK_TRANSACTION_COMPLETE_ID = `${STEDI_EXTENSION_BASE}/webhook-transaction-complete`;

/** 277CA status category codes that indicate acceptance */
export const ACCEPTED_STATUS_CATEGORY_CODES = ['A0', 'A1'] as const;

/** 277CA status category codes that indicate rejection */
export const REJECTED_STATUS_CATEGORY_CODES = ['A3', 'A7'] as const;

/** X12 transaction set identifiers */
export const TRANSACTION_SET_277 = '277';
export const TRANSACTION_SET_835 = '835';

/** Stedi report API base URLs */
export const STEDI_REPORT_277_URL = `https://healthcare.us.stedi.com/2024-04-01/healthcare/reports/${TRANSACTION_SET_277}`;
export const STEDI_REPORT_835_URL = `https://healthcare.us.stedi.com/2024-04-01/healthcare/reports/${TRANSACTION_SET_835}`;
export const STEDI_CLAIM_STATUS_URL = 'https://healthcare.us.stedi.com/2024-04-01/change/medicalnetwork/claimstatus/v2';
